import React from 'react';
import { Card, Typography, Space, Divider, Tag, Button, message } from 'antd';
import { CopyOutlined, FileTextOutlined, PictureOutlined, SettingOutlined, CodeOutlined } from '@ant-design/icons';
import { getStaticPath, staticPathExamples } from '../utils';

const { Title, Paragraph, Text, Link } = Typography;

/**
 * 静态路径处理演示页面
 */
const StaticPathDemo: React.FC = () => {
  const currentEnv = process.env.NODE_ENV;

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success('已复制到剪贴板');
    });
  };

  const examples = [
    {
      title: '文本文件',
      icon: <FileTextOutlined />,
      originalPath: '/template-app/static-files/1.txt',
      resolvedPath: staticPathExamples.textFile(),
      description: '处理文本文件路径'
    },
    {
      title: '图片文件',
      icon: <PictureOutlined />,
      originalPath: '/template-app/static-files/images/logo.png',
      resolvedPath: staticPathExamples.imageFile(),
      description: '处理图片文件路径'
    },
    {
      title: '配置文件',
      icon: <SettingOutlined />,
      originalPath: '/template-app/static-config/app.json',
      resolvedPath: staticPathExamples.configFile(),
      description: '处理配置文件路径'
    },
    {
      title: 'CSS文件',
      icon: <CodeOutlined />,
      originalPath: '/template-app/static-files/styles/custom.css',
      resolvedPath: staticPathExamples.cssFile(),
      description: '处理样式文件路径'
    },
    {
      title: 'JavaScript文件',
      icon: <CodeOutlined />,
      originalPath: '/template-app/static-files/scripts/utils.js',
      resolvedPath: staticPathExamples.jsFile(),
      description: '处理脚本文件路径'
    }
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Title level={2}>静态路径处理演示</Title>
      
      <Card className="mb-6">
        <Space direction="vertical" size="middle" className="w-full">
          <div>
            <Text strong>当前环境：</Text>
            <Tag color={currentEnv === 'development' ? 'green' : 'blue'}>
              {currentEnv === 'development' ? '开发环境' : '生产环境'}
            </Tag>
          </div>
          
          <Paragraph>
            这个演示展示了 <Text code>resolveStaticPath</Text> 函数的使用方法。
            该函数会根据当前环境自动处理静态文件路径：
          </Paragraph>
          
          <ul className="list-disc list-inside space-y-2 ml-4">
            <li><Text strong>开发环境：</Text>去掉应用前缀，例如 <Text code>/template-app/static-files/1.txt</Text> → <Text code>/static-files/1.txt</Text></li>
            <li><Text strong>生产环境：</Text>保留完整路径，例如 <Text code>/template-app/static-files/1.txt</Text> → <Text code>/template-app/static-files/1.txt</Text></li>
          </ul>
        </Space>
      </Card>

      <Title level={3}>使用示例</Title>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {examples.map((example, index) => (
          <Card 
            key={index}
            title={
              <Space>
                {example.icon}
                {example.title}
              </Space>
            }
            size="small"
            className="h-full"
          >
            <Space direction="vertical" size="small" className="w-full">
              <div>
                <Text type="secondary" className="text-xs">原始路径：</Text>
                <div className="flex items-center justify-between bg-gray-50 p-2 rounded mt-1">
                  <Text code className="text-xs break-all">{example.originalPath}</Text>
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(example.originalPath)}
                  />
                </div>
              </div>
              
              <div>
                <Text type="secondary" className="text-xs">处理后路径：</Text>
                <div className="flex items-center justify-between bg-blue-50 p-2 rounded mt-1">
                  <Text code className="text-xs break-all text-blue-600">{example.resolvedPath}</Text>
                  <Button 
                    type="text" 
                    size="small" 
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(example.resolvedPath)}
                  />
                </div>
              </div>
              
              <Text type="secondary" className="text-xs">{example.description}</Text>
            </Space>
          </Card>
        ))}
      </div>

      <Divider />

      <Card title="代码示例" className="mt-6">
        <Space direction="vertical" size="middle" className="w-full">
          <div>
            <Text strong>基本使用：</Text>
            <pre className="bg-gray-50 p-4 rounded mt-2 overflow-x-auto">
              <code>{`import { getStaticPath } from '../utils';

// 使用便捷方法（已预设应用名称）
const path1 = getStaticPath('/template-app/static-files/1.txt');

// 或者直接使用原始函数
import { resolveStaticPath } from '@packages/shared';
const path2 = resolveStaticPath('/template-app/static-files/1.txt', 'template-app');`}</code>
            </pre>
          </div>
          
          <div>
            <Text strong>在组件中使用：</Text>
            <pre className="bg-gray-50 p-4 rounded mt-2 overflow-x-auto">
              <code>{`function MyComponent() {
  const imagePath = getStaticPath('/template-app/static-files/images/logo.png');
  
  return (
    <img src={imagePath} alt="Logo" />
  );
}`}</code>
            </pre>
          </div>
        </Space>
      </Card>

      <Card title="函数说明" className="mt-6">
        <Space direction="vertical" size="middle" className="w-full">
          <div>
            <Text strong>resolveStaticPath 参数：</Text>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li><Text code>path</Text>: 原始路径字符串</li>
              <li><Text code>appName</Text>: 应用名称（如 'template-app'）</li>
              <li><Text code>isDev</Text>: 是否为开发环境（可选，默认根据 NODE_ENV 判断）</li>
            </ul>
          </div>
          
          <div>
            <Text strong>getStaticPath 参数：</Text>
            <ul className="list-disc list-inside space-y-1 ml-4 mt-2">
              <li><Text code>path</Text>: 原始路径字符串（已预设应用名称为 'template-app'）</li>
            </ul>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default StaticPathDemo;
